/**
 * TopShelf custom card/PDP enhancements.
 *
 * NOTE: the material-style ripple that used to run on product/resource/gallery
 * cards was intentionally removed — it added click-handler + layout work to every
 * card tap and hurt tap responsiveness. The Kith MENU keeps its own, separate
 * ripple (the K() helper in menu-drawer-kith-v5.js). The lightweight homepage
 * carousels below use delegated hydration and controls, not per-card tap effects.
 */

(function initTopshelfInteractionGatedGorgias() {
  if (window.__topshelfGorgiasInteractionGateBound) return;
  window.__topshelfGorgiasInteractionGateBound = true;

  const events = ['scroll', 'pointerdown', 'keydown', 'touchstart'];
  const loaders = [
    {
      attribute: 'data-gorgias-loader-chat',
      src: 'https://config.gorgias.chat/bundle-loader/shopify/763aab.myshopify.com',
    },
    {
      attribute: 'data-gorgias-loader-convert',
      src: 'https://content.9gtb.com/loader.js',
    },
    {
      attribute: 'data-gorgias-loader-mailto-replace',
      src: 'https://config.gorgias.help/api/contact-forms/replace-mailto-script.js?shopName=763aab',
    },
  ];
  let started = false;

  function removeListeners() {
    events.forEach((name) => window.removeEventListener(name, loadGorgias));
  }

  function loadGorgias() {
    if (started) return;
    started = true;
    removeListeners();

    loaders.forEach(({ attribute, src }) => {
      const script = document.createElement('script');
      script.setAttribute(attribute, '');
      script.src = src;
      script.async = true;
      document.head.appendChild(script);
    });
  }

  if (
    document.querySelector(
      '[data-gorgias-loader-chat], [data-gorgias-loader-convert], [data-gorgias-loader-mailto-replace]'
    )
  )
    return;

  events.forEach((name) => window.addEventListener(name, loadGorgias, { passive: true }));
})();

(function initTopshelfMobileCardVideoGuard() {
  if (window.__topshelfMobileCardVideoGuardBound) return;
  window.__topshelfMobileCardVideoGuardBound = true;

  const mq = window.matchMedia('(max-width: 749px)');
  if (!mq.matches) return;

  function removeCardVideos(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('product-card[data-thumb-video]').forEach((card) => {
      card.removeAttribute('data-thumb-video');
    });
    scope.querySelectorAll('.coral-card-video').forEach((video) => {
      try {
        video.pause();
      } catch (e) {}
      video.remove();
    });
  }

  const boot = () => removeCardVideos(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (e) => removeCardVideos(e.target));
})();

(function initTopshelfLightweightCarousels() {
  if (window.__topshelfLightweightCarouselsBound) return;
  window.__topshelfLightweightCarouselsBound = true;

  const CAROUSEL_SELECTOR = '[data-topshelf-light-carousel]';
  const CARD_SELECTOR = '[data-topshelf-light-card]';
  const mobileMedia = window.matchMedia('(max-width: 749px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  function hydrateImage(image) {
    if (!(image instanceof HTMLImageElement) || image.dataset.topshelfHydrated === 'true') return;

    image.dataset.topshelfHydrated = 'true';
    image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });

    if (image.dataset.sizes) image.sizes = image.dataset.sizes;
    if (image.dataset.srcset) image.srcset = image.dataset.srcset;
    if (image.dataset.src) image.src = image.dataset.src;
    image.loading = 'eager';

    delete image.dataset.sizes;
    delete image.dataset.srcset;
    delete image.dataset.src;

    if (image.complete) image.classList.add('is-loaded');
  }

  const imageObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              hydrateImage(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '300px 300px', threshold: 0.01 }
        )
      : null;

  const deferredImageObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              hydrateImage(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '100px 0px', threshold: 0.01 }
        )
      : null;

  const bentoDeferredImageObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.intersectionRatio < 0.6) return;
              hydrateImage(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px', threshold: 0.6 }
        )
      : null;

  function observeDeferredImage(image) {
    if (image.matches('[data-topshelf-bento-secondary]') && bentoDeferredImageObserver) {
      bentoDeferredImageObserver.observe(image);
    } else if (deferredImageObserver) {
      deferredImageObserver.observe(image);
    } else {
      hydrateImage(image);
    }
  }

  function bindDeferredImages(root = document) {
    if (
      root instanceof HTMLImageElement &&
      root.matches('[data-topshelf-deferred-image][data-src]')
    ) {
      observeDeferredImage(root);
    }

    root.querySelectorAll?.('[data-topshelf-deferred-image][data-src]').forEach((image) => {
      observeDeferredImage(image);
    });
  }

  function videosAllowed() {
    return !(
      mobileMedia.matches ||
      reducedMotionMedia.matches ||
      connection?.saveData ||
      ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)
    );
  }

  function pauseCardVideo(card) {
    const video = card.querySelector('.topshelf-light-card__video');
    if (video instanceof HTMLVideoElement) video.pause();
  }

  function playCardVideo(card) {
    if (!videosAllowed()) return;

    const videoUrl = card.dataset.thumbVideo;
    const media = card.querySelector('.topshelf-light-card__media');
    if (!videoUrl || !media) return;

    let video = card.querySelector('.topshelf-light-card__video');
    if (!(video instanceof HTMLVideoElement)) {
      video = document.createElement('video');
      video.className = 'topshelf-light-card__video';
      video.src = videoUrl;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.autoplay = true;
      video.preload = 'none';
      video.setAttribute('aria-hidden', 'true');
      media.appendChild(video);
    }

    video.play().catch(() => {});
  }

  const videoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) playCardVideo(entry.target);
              else pauseCardVideo(entry.target);
            });
          },
          { rootMargin: '150px 0px', threshold: 0.6 }
        )
      : null;

  function removeDisallowedVideos() {
    if (videosAllowed()) return;
    document.querySelectorAll('.topshelf-light-card__video').forEach((video) => {
      try {
        video.pause();
      } catch (error) {}
      video.remove();
    });
  }

  function bindCarouselCardMedia(root) {
    root.querySelectorAll('.topshelf-light-card__image[data-src]').forEach((image) => {
      if (imageObserver) imageObserver.observe(image);
      else hydrateImage(image);
    });

    root.querySelectorAll(`${CARD_SELECTOR}[data-thumb-video]`).forEach((card) => {
      if (videoObserver) videoObserver.observe(card);
    });
  }

  function bindCarousel(carousel) {
    if (!(carousel instanceof HTMLElement) || carousel.dataset.topshelfCarouselBound === 'true') return;
    carousel.dataset.topshelfCarouselBound = 'true';

    const viewport = carousel.querySelector('[data-topshelf-light-carousel-viewport]');
    const previous = carousel.querySelector('[data-topshelf-light-carousel-previous]');
    const next = carousel.querySelector('[data-topshelf-light-carousel-next]');
    if (!(viewport instanceof HTMLElement)) return;

    bindCarouselCardMedia(carousel);

    let updateFrame = 0;
    const updateControls = () => {
      cancelAnimationFrame(updateFrame);
      updateFrame = requestAnimationFrame(() => {
        // Batch every layout read before changing either button. Writing
        // `disabled` can invalidate styles, so a later clientWidth/scrollWidth
        // read in the same frame would otherwise force synchronous layout.
        const scrollLeft = viewport.scrollLeft;
        const clientWidth = viewport.clientWidth;
        const scrollWidth = viewport.scrollWidth;
        const atStart = scrollLeft <= 2;
        const atEnd = Math.ceil(scrollLeft + clientWidth) >= scrollWidth - 2;

        if (previous instanceof HTMLButtonElement) previous.disabled = atStart;
        if (next instanceof HTMLButtonElement) {
          next.disabled = atEnd;
        }
      });
    };

    const scrollCarousel = (direction) => {
      const distance = Math.max(160, viewport.clientWidth * 0.85);
      viewport.scrollBy({
        left: direction * distance,
        behavior: reducedMotionMedia.matches ? 'auto' : 'smooth',
      });
    };

    previous?.addEventListener('click', () => scrollCarousel(-1));
    next?.addEventListener('click', () => scrollCarousel(1));
    viewport.addEventListener('scroll', updateControls, { passive: true });

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScrollLeft = 0;
    let suppressClick = false;

    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScrollLeft = viewport.scrollLeft;
    });

    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > 4) {
        moved = true;
        viewport.classList.add('is-dragging');
        viewport.setPointerCapture(event.pointerId);
      }
      if (!moved) return;
      event.preventDefault();
      viewport.scrollLeft = startScrollLeft - delta;
    });

    const finishDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      suppressClick = moved;
      viewport.classList.remove('is-dragging');
      if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
      window.setTimeout(() => {
        suppressClick = false;
      }, 50);
    };

    viewport.addEventListener('pointerup', finishDrag);
    viewport.addEventListener('pointercancel', finishDrag);
    viewport.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    const deferredCards = carousel.querySelector('template[data-topshelf-light-carousel-rest]');
    if (deferredCards instanceof HTMLTemplateElement) {
      const hydrateRemainingCards = () => {
        if (!deferredCards.isConnected) return;
        deferredCards.replaceWith(deferredCards.content);
        bindCarouselCardMedia(carousel);
      };

      if ('IntersectionObserver' in window) {
        const carouselObserver = new IntersectionObserver(
          (entries, observer) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            hydrateRemainingCards();
          },
          { rootMargin: '300px 0px', threshold: 0.01 }
        );
        carouselObserver.observe(carousel);
      } else {
        hydrateRemainingCards();
      }
    }
  }

  function bindAll(root = document) {
    bindDeferredImages(root);
    if (root instanceof Element && root.matches(CAROUSEL_SELECTOR)) bindCarousel(root);
    root.querySelectorAll?.(CAROUSEL_SELECTOR).forEach(bindCarousel);
  }

  const boot = () => bindAll(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => bindAll(event.target));
  mobileMedia.addEventListener('change', removeDisallowedVideos);
  reducedMotionMedia.addEventListener('change', removeDisallowedVideos);
})();

(function initTopshelfDescriptionAutoScroll() {
  if (window.__topshelfDescriptionAutoScrollBound) return;
  window.__topshelfDescriptionAutoScrollBound = true;

  const CONTROL_SELECTOR = '.desc-accordion__toggle, .desc-accordion__icon-button';
  if (!document.querySelector('.desc-accordion')) return;

  function getAccordion(target) {
    return target && target.closest ? target.closest('.desc-accordion') : null;
  }

  function scrollToProductTop() {
    // Horizon scrolls .page-wrapper instead of the document at 990px and up.
    const wrapper = document.querySelector('.page-wrapper');
    const container =
      wrapper && getComputedStyle(wrapper).overflowY === 'auto'
        ? wrapper
        : document.scrollingElement || document.documentElement;
    container.scrollTo({ top: 0, behavior: 'smooth' });
  }

  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest(CONTROL_SELECTOR);
      const accordion = getAccordion(control);
      if (!accordion) return;

      const isClosing = accordion.classList.contains('is-open');
      if (!isClosing) return;

      window.setTimeout(() => {
        if (!accordion.classList.contains('is-open')) {
          scrollToProductTop();
        }
      }, 80);

      // Run once more after the accordion height transition settles so the
      // browser cannot strand the viewport near the footer after the collapse.
      window.setTimeout(() => {
        if (!accordion.classList.contains('is-open')) {
          scrollToProductTop();
        }
      }, 420);
    },
    true
  );
})();

(function initTopshelfPdpPurchaseLayout() {
  if (window.__topshelfPdpPurchaseLayoutBound) return;
  window.__topshelfPdpPurchaseLayoutBound = true;

  const ENDPOINT = 'https://storesboost-steven-beivue.laravel.cloud/api/public/wish-list';
  const TOKEN = 'PtAJM1HyT7BOFQdd3ROw1PmsrqedqTk0ZJU3DLSt';
  const IS_PDP = !!document.querySelector('.product-details, product-form-component');
  if (!IS_PDP) return;

  function getCurrentVariantGid(btn, fallbackId) {
    const scope = btn.closest('product-form-component, .shopify-section') || document;
    const input = scope.querySelector('form[action*="/cart/add"] [name="id"]');
    const id = input ? input.value : fallbackId;
    return String(id).startsWith('gid://shopify/ProductVariant/')
      ? id
      : `gid://shopify/ProductVariant/${id}`;
  }

  function updateHeaderWishlistCount(count, variantIds) {
    if (Array.isArray(variantIds) && window.__topshelfSyncWishlistCount) {
      window.__topshelfSyncWishlistCount(variantIds);
      return;
    }

    if (!Number.isFinite(count)) return;

    document.querySelectorAll('.kith-menu-wishlist-bubble').forEach((bubble) => {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.classList.toggle('visually-hidden', count === 0);
    });
    try {
      localStorage.setItem('topshelf:wishlist-valid-count', String(count));
    } catch (_) {}
    document.documentElement.style.removeProperty('--topshelf-wishlist-cached-count');
    document.documentElement.dataset.topshelfWishlistReady = 'true';
  }

  function attachBehavior(btn) {
    const customer = {
      id: btn.dataset.customerId,
      email: btn.dataset.customerEmail,
    };
    const fallbackVariantId = btn.dataset.variantId;

    const setButtonState = (active) => {
      btn.classList.toggle('is-active', active);
      btn.querySelector('.wishlist-text').textContent = active ? '♥ Wish Listed' : '♡ Wish List';
    };

    const readWishlist = async () => {
      try {
        const res = await fetch(`${ENDPOINT}?customer_id=${encodeURIComponent(customer.id)}`, {
          headers: { Accept: 'application/json', 'X-Wishlist-Token': TOKEN },
        });
        if (!res.ok) return;
        const data = await res.json();
        const gid = getCurrentVariantGid(btn, fallbackVariantId);
        setButtonState((data?.wishlist?.variant_ids || []).includes(gid));
        updateHeaderWishlistCount(
          Number(data?.wishlist?.items_count),
          data?.wishlist?.variant_ids,
        );
      } catch (e) {
        console.error(e);
      }
    };

    const toggleWishlist = async () => {
      const variant_id = getCurrentVariantGid(btn, fallbackVariantId);
      const wasActive = btn.classList.contains('is-active');
      setButtonState(!wasActive);
      btn.disabled = true;
      try {
        const res = await fetch(`${ENDPOINT}/items`, {
          method: wasActive ? 'DELETE' : 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Wishlist-Token': TOKEN,
          },
          body: JSON.stringify({ customer, variant_id }),
        });
        const data = await res.json();
        if (!res.ok || data?.ok === false) {
          setButtonState(wasActive);
          console.error(data);
          return;
        }

        const variantIds = data?.wishlist?.variant_ids;
        setButtonState(Array.isArray(variantIds) ? variantIds.includes(variant_id) : !wasActive);
        updateHeaderWishlistCount(Number(data?.wishlist?.items_count), variantIds);
      } catch (e) {
        setButtonState(wasActive);
        console.error(e);
      } finally {
        btn.disabled = false;
      }
    };

    btn.addEventListener('click', toggleWishlist);
    readWishlist();

    document.addEventListener('change', (e) => {
      if (e.target.matches('form[action*="/cart/add"] [name="id"]')) readWishlist();
    });
  }

  function enhance() {
    document.querySelectorAll('.wishlist-button[data-customer-id]:not([data-ts-wl-bound])').forEach((btn) => {
      btn.dataset.tsWlBound = '1';
      attachBehavior(btn);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance, { once: true });
  } else {
    enhance();
  }

  document.addEventListener('shopify:section:load', enhance);
  document.addEventListener('shopify:product:select', (event) => {
    const promise = event?.promise;
    if (promise && typeof promise.finally === 'function') {
      promise.finally(() => requestAnimationFrame(enhance));
    } else {
      requestAnimationFrame(enhance);
    }
  });
})();

(function initTopshelfWishlistCountSync() {
  if (window.__topshelfWishlistCountSyncBound) return;
  window.__topshelfWishlistCountSyncBound = true;

  const ENDPOINT = 'https://storesboost-steven-beivue.laravel.cloud/api/public/wish-list';
  const TOKEN = 'PtAJM1HyT7BOFQdd3ROw1PmsrqedqTk0ZJU3DLSt';
  const customer = window._kithRewardsCustomer || window.rewardsCustomer;
  const rawCustomerId = customer?.customerId;
  if (!customer?.isLoggedIn || !rawCustomerId) {
    try {
      localStorage.removeItem('topshelf:wishlist-valid-count');
    } catch (_) {}
    document.documentElement.style.removeProperty('--topshelf-wishlist-cached-count');
    document.documentElement.dataset.topshelfWishlistReady = 'true';
    return;
  }

  const customerId = String(rawCustomerId).startsWith('gid://shopify/Customer/')
    ? String(rawCustomerId)
    : `gid://shopify/Customer/${rawCustomerId}`;

  function setCount(count) {
    if (!Number.isFinite(count)) return;

    document.querySelectorAll('.kith-menu-wishlist-bubble').forEach((bubble) => {
      bubble.textContent = count > 99 ? '99+' : String(count);
      bubble.classList.toggle('visually-hidden', count === 0);
    });
    try {
      localStorage.setItem('topshelf:wishlist-valid-count', String(count));
    } catch (_) {}
    document.documentElement.style.removeProperty('--topshelf-wishlist-cached-count');
    document.documentElement.dataset.topshelfWishlistReady = 'true';
    window.__topshelfWishlistCount = count;
  }

  async function getValidCount(variantIds) {
    const ids = [...new Set(Array.isArray(variantIds) ? variantIds : [])];
    if (!ids.length) return 0;

    const response = await fetch('/api/2026-01/graphql.json', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: 'query TopShelfWishlistCount($ids:[ID!]!){nodes(ids:$ids){... on ProductVariant{id}}}',
        variables: { ids },
      }),
    });
    const data = await response.json();
    if (!response.ok || data?.errors) {
      throw new Error(data?.errors?.[0]?.message || 'Unable to validate wishlist count.');
    }

    return (data?.data?.nodes || []).filter(Boolean).length;
  }

  async function syncCount(providedVariantIds) {
    try {
      let variantIds = providedVariantIds;
      if (!Array.isArray(variantIds)) {
        const response = await fetch(`${ENDPOINT}?customer_id=${encodeURIComponent(customerId)}`, {
          headers: {
            Accept: 'application/json',
            'X-Wishlist-Token': TOKEN,
          },
        });
        const data = await response.json();
        if (!response.ok || data?.ok === false) {
          throw new Error(data?.message || 'Unable to load wishlist count.');
        }
        variantIds = data?.wishlist?.variant_ids || [];
      }

      const count = await getValidCount(variantIds);
      setCount(count);
      return count;
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  window.__topshelfSyncWishlistCount = syncCount;
  syncCount();
})();

/**
 * TopShelf: keep the "SAVE $X" dollar-off badge in sync with the selected variant.
 *
 * The badge is a static custom-liquid block (snippets/topshelf-dollar-off-badge.liquid)
 * rendered with selected_or_first_available_variant, so it is only correct on initial
 * page load. Horizon's variant-picker re-fetches the product page and dispatches the
 * bubbling `shopify:product:select` event, whose `promise` resolves with the parsed new
 * HTML — we mirror the freshly rendered badge markup into the on-page wrapper. An empty
 * wrapper (no discount on the newly selected variant) naturally hides the badge. The
 * update is scoped to the section/dialog that fired the event, so a quick-add or product
 * card elsewhere on the page can never overwrite the main product's badge.
 */
(function initTopshelfDollarOffBadgeSync() {
  if (window.__topshelfDollarOffBadgeSyncBound) return;
  window.__topshelfDollarOffBadgeSyncBound = true;

  const BLOCK = '.topshelf-dollar-off-badge-block';

  document.addEventListener('shopify:product:select', (event) => {
    const promise = event?.promise;
    if (!promise || typeof promise.then !== 'function') return;
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const scope = event.target.closest('.shopify-section, dialog');
    if (!scope || !scope.querySelector(BLOCK)) return;

    promise
      .then(({ detail }) => {
        const newHtml = detail?.html;
        if (!newHtml || !newHtml.querySelector) return;

        const newBlock = newHtml.querySelector(BLOCK);
        if (!newBlock) return;

        scope.querySelectorAll(BLOCK).forEach((currentBlock) => {
          currentBlock.innerHTML = newBlock.innerHTML;
        });
      })
      .catch(() => {});
  });
})();

(function initTopshelfPdpRewardsSync() {
  if (window.__topshelfPdpRewardsSyncBound) return;
  window.__topshelfPdpRewardsSyncBound = true;

  const TITLE = '.topshelf-pdp-rewards__title';
  if (!document.querySelector(TITLE)) return;

  document.addEventListener('shopify:product:select', (event) => {
    const promise = event?.promise;
    if (!promise || typeof promise.then !== 'function') return;

    const section = event.target instanceof Element ? event.target.closest('.shopify-section, dialog') : null;
    const title = (section || document).querySelector(TITLE);
    if (!title) return;

    promise
      .then(({ detail }) => {
        const price = Number(detail?.resource?.price);
        if (!Number.isFinite(price)) return;

        title.textContent = `Earn ${Math.round(price * 0.05)} Reefer Rewards Points`;
      })
      .catch(() => {});
  });
})();

/**
 * TopShelf: keep the PDP pay-later sentence in sync with the selected variant.
 *
 * snippets/topshelf-product-installments.liquid renders the Shop Pay installment amount
 * in Liquid from selected_or_first_available_variant, so (like the dollar-off badge) the
 * freshly rendered sentence from Horizon's `shopify:product:select` HTML is mirrored in,
 * scoped to the section/dialog that fired the event.
 */
(function initTopshelfPdpInstallmentsSync() {
  if (window.__topshelfPdpInstallmentsSyncBound) return;
  window.__topshelfPdpInstallmentsSyncBound = true;

  const TERMS = '.topshelf-product-installments__terms';
  if (!document.querySelector(TERMS)) return;

  document.addEventListener('shopify:product:select', (event) => {
    const promise = event?.promise;
    if (!promise || typeof promise.then !== 'function') return;
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const scope = event.target.closest('.shopify-section, dialog');
    if (!scope || !scope.querySelector(TERMS)) return;

    promise
      .then(({ detail }) => {
        const newHtml = detail?.html;
        if (!newHtml || !newHtml.querySelector) return;

        const newTerms = newHtml.querySelector(TERMS);
        if (!newTerms) return;

        scope.querySelectorAll(TERMS).forEach((currentTerms) => {
          currentTerms.innerHTML = newTerms.innerHTML;
        });
      })
      .catch(() => {});
  });
})();

/**
 * TopShelf: Subscribe & Save enhancements for the Shopify Subscriptions app block.
 *
 *   - Injects a benefits checklist (incl. a dynamic "Save $X every order" line) into
 *     each variant's subscribe card. Each variant renders its own
 *     .shopify_subscriptions_app_block section (hidden ones get --hidden), and every
 *     option carries data-variant-price / data-variant-compare-at-price, so the
 *     savings are computed per section once — no MutationObserver needed.
 *   - Pre-selects the first Subscribe & Save plan on the visible section so the page
 *     defaults to subscribe; users opt out by clicking One-time purchase.
 *   - Re-applies the default on variant change (the vendor resets to one-time), unless
 *     the shopper has explicitly made their own purchase-option choice.
 */
(function initTopshelfSubscriptions() {
  if (window.__topshelfSubscriptionsBound) return;
  window.__topshelfSubscriptionsBound = true;

  // When any variant is discounted, a server-side guard hides the entire Subscribe & Save
  // widget. Skip all enhancement/preselect so we never silently check a hidden subscription
  // plan — add-to-cart must stay a normal one-time purchase in that case.
  if (document.querySelector('.topshelf-sub-sale-guard')) return;

  const CONTAINER = '.shopify_subscriptions_app_container';
  const SECTION = '.shopify_subscriptions_app_block';
  const HIDDEN = 'shopify_subscriptions_app_block--hidden';
  const IS_PDP = !!document.querySelector('.product-details, product-form-component, ' + CONTAINER + ', ' + SECTION);
  if (!IS_PDP) return;

  function parseMoney(str) {
    if (!str) return NaN;
    const n = parseFloat(String(str).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? NaN : n;
  }
  function moneySymbol(str) {
    const m = String(str || '').match(/^\s*([^\d\s]+)/);
    return m ? m[1] : '$';
  }
  function formatMoney(symbol, value) {
    const v = Math.round(value * 100) / 100;
    return symbol + (v % 1 === 0 ? String(v) : v.toFixed(2));
  }

  function subscribeGroup(section) {
    const groups = section.querySelectorAll('.subscription_group');
    for (const g of groups) {
      if (!g.querySelector('.one_time_purchase_option_app_block')) return g;
    }
    return null;
  }

  function maxSavings(section) {
    const radios = section.querySelectorAll('input[data-radio-type="selling_plan"]');
    let best = 0;
    let symbol = '$';
    radios.forEach((r) => {
      const price = parseMoney(r.getAttribute('data-variant-price'));
      const compare = parseMoney(r.getAttribute('data-variant-compare-at-price'));
      if (!isNaN(price) && !isNaN(compare) && compare > price) {
        symbol = moneySymbol(r.getAttribute('data-variant-price'));
        best = Math.max(best, compare - price);
      }
    });
    return { amount: best, symbol: symbol };
  }

  function injectBenefits(section) {
    if (section.dataset.tsSubEnhanced) return;
    const group = subscribeGroup(section);
    if (!group) return;
    const { amount, symbol } = maxSavings(section);
    if (amount <= 0) return;

    section.dataset.tsSubEnhanced = '1';
    const ul = document.createElement('ul');
    ul.className = 'topshelf-sub-benefits';
    const items = [
      'Save up to <strong>' + formatMoney(symbol, amount) + '</strong> on every order',
      'Delivered automatically on your schedule',
      'Skip, pause, or cancel anytime — no fees',
    ];
    items.forEach((html) => {
      const li = document.createElement('li');
      li.innerHTML = html;
      ul.appendChild(li);
    });
    group.appendChild(ul);
  }

  function visibleSection(root) {
    const sections = root.querySelectorAll(SECTION);
    for (const s of sections) {
      if (!s.classList.contains(HIDDEN)) return s;
    }
    return sections[0] || null;
  }

  function preselectSubscribe(section) {
    if (!section || window.__topshelfSubUserChose) return;
    const sub = section.querySelector('input[data-radio-type="selling_plan"]');
    if (sub && !sub.checked) {
      sub.checked = true;
      sub.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function reveal() {
    document.documentElement.classList.add('ts-sub-ready');
  }

  function applyAll() {
    document.querySelectorAll(CONTAINER).forEach((root) => {
      root.querySelectorAll(SECTION).forEach(injectBenefits);
      preselectSubscribe(visibleSection(root));
    });
  }

  function reapplyVisible() {
    window.setTimeout(applyAll, 60);
  }

  // Stop overriding once the shopper makes their own real choice (incl. one-time).
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('input[name^="purchaseOption"]')) {
      if (e.isTrusted) {
        // Real shopper choice — respect it, even if that's one-time.
        window.__topshelfSubUserChose = true;
      } else if (t.matches('input[data-radio-type="one_time_purchase"]')) {
        // Vendor programmatically reset to one-time → restore the subscribe default.
        reapplyVisible();
      }
      return;
    }
    // Any other change inside a product form (e.g. variant/size) → the vendor rebuilds
    // and resets to one-time, so re-apply the subscribe default to the now-visible card.
    reapplyVisible();
  });

  function boot() {
    applyAll();
    // Run one more pass on the next frame to win the vendor's deferred hydration race
    // while the widget is still hidden, then reveal — so the shopper only ever sees
    // Subscribe pre-selected (no visible one-time → subscribe flip).
    requestAnimationFrame(() => {
      applyAll();
      requestAnimationFrame(reveal);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    applyAll();
    reveal();
  });
})();

(function initTopshelfReviews() {
  if (window.__topshelfReviewsBound) return;
  window.__topshelfReviewsBound = true;

  function initReviews(root) {
    if (!(root instanceof HTMLElement) || root.dataset.topshelfReviewsBound === 'true') return;
    var configNode = root.querySelector('[data-topshelf-reviews-config]');
    if (!configNode) return;
    var config;
    try { config = JSON.parse(configNode.textContent); } catch (error) { return; }
    root.dataset.topshelfReviewsBound = 'true';

    var mount = document.getElementById(config.mountId);
    if (!root || !mount || mount.getAttribute('data-topshelf-reviews') === 'started') return;
    mount.setAttribute('data-topshelf-reviews', 'started');

    var designMode = config.designMode;
    var minRating = config.minRating;
    var autoScroll = config.autoScroll;
    if (!autoScroll || (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)) {
      autoScroll = false;
    }

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    var reviewsioPanel = root.querySelector('[data-topshelf-panel="reviewsio"]');
    var nearViewport = false;
    var started = false;

    function fail() {
      mount.removeAttribute('aria-busy');
      if (designMode) {
        mount.innerHTML = '<p class="topshelf-reviews__notice">REVIEWS.io carousel did not load. Check the store key and review filters. (Only shown in the theme editor.)</p>';
      } else if (tabs.length > 1) {
        var reviewsioTab = root.querySelector('[data-topshelf-tab="reviewsio"]');
        var wasShowing = !reviewsioPanel.hidden;
        tabs = tabs.filter(function (tab) { return tab !== reviewsioTab; });
        reviewsioTab.remove();
        reviewsioPanel.remove();
        syncSourceCount();
        if (wasShowing) selectTab(tabs[0], false);
      } else {
        root.hidden = true;
      }
    }

    function syncSourceCount() {
      var tablist = root.querySelector('[role="tablist"]');
      if (!tablist) return;
      tablist.style.setProperty('--topshelf-source-count', tabs.length);
      tablist.hidden = tabs.length < 2;
    }

    function hydratePanel(panel) {
      if (!panel) return;
      var template = panel.querySelector('template[data-topshelf-panel-template]');
      if (!template) return;
      template.replaceWith(template.content);
      bindExternalReviewViewports(panel);
    }

    function selectTab(tab, focus) {
      tabs.forEach(function (item) {
        var selected = item === tab;
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
        var panel = document.getElementById(item.getAttribute('aria-controls'));
        if (panel) {
          if (selected) hydratePanel(panel);
          panel.hidden = !selected;
        }
      });
      if (focus) tab.focus();
      if (tab.getAttribute('data-topshelf-tab') === 'reviewsio') {
        maybeStart();
        window.dispatchEvent(new Event('resize'));
      }
      if (tab.getAttribute('data-topshelf-tab') === 'google') loadGoogleLive();
      updateArrows();
    }

    var googleLivePanel = root.querySelector('[data-topshelf-google-live]');
    var googleLiveRequested = false;

    function removeGoogleTab() {
      var googleTab = root.querySelector('[data-topshelf-tab="google"]');
      if (!googleTab) return;
      var wasShowing = googleLivePanel && !googleLivePanel.hidden;
      tabs = tabs.filter(function (tab) { return tab !== googleTab; });
      googleTab.remove();
      if (googleLivePanel) googleLivePanel.remove();
      syncSourceCount();
      if (wasShowing && tabs.length) selectTab(tabs[0], false);
    }

    var GOOGLE_LOGO_SVG = '<svg viewBox="0 0 48 48" width="15" height="15" focusable="false">' +
      '<path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.4-.4-3.5z"/>' +
      '<path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>' +
      '<path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/>' +
      '<path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>' +
      '</svg>';

    function buildGoogleCard(review) {
      var author = review.authorAttribution || {};
      var card = document.createElement('li');
      card.className = 'topshelf-ext-reviews__card';

      var head = document.createElement('div');
      head.className = 'topshelf-ext-reviews__author';
      var avatar;
      if (author.photoUri) {
        avatar = document.createElement('img');
        avatar.src = author.photoUri;
        avatar.alt = '';
        avatar.width = 40;
        avatar.height = 40;
        avatar.loading = 'lazy';
        avatar.decoding = 'async';
        avatar.referrerPolicy = 'no-referrer';
      } else {
        avatar = document.createElement('span');
        avatar.className = 'topshelf-ext-reviews__avatar--initial';
        avatar.setAttribute('aria-hidden', 'true');
        avatar.textContent = (author.displayName || 'G').charAt(0).toUpperCase();
      }
      avatar.classList.add('topshelf-ext-reviews__avatar');
      head.appendChild(avatar);

      var meta = document.createElement('span');
      meta.className = 'topshelf-ext-reviews__author-meta';
      var name = document.createElement(author.uri ? 'a' : 'span');
      name.className = 'topshelf-ext-reviews__author-name';
      name.textContent = author.displayName || 'Google user';
      if (author.uri) {
        name.href = author.uri;
        name.target = '_blank';
        name.rel = 'noopener';
      }
      meta.appendChild(name);
      if (review.relativePublishTimeDescription) {
        var date = document.createElement('time');
        date.className = 'topshelf-ext-reviews__date';
        if (review.publishTime) date.dateTime = review.publishTime;
        date.textContent = review.relativePublishTimeDescription;
        meta.appendChild(date);
      }
      head.appendChild(meta);
      card.appendChild(head);

      var stars = document.createElement('span');
      stars.className = 'topshelf-ext-reviews__stars';
      stars.style.setProperty('--topshelf-rating', review.rating);
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', review.rating + ' out of 5 stars');
      card.appendChild(stars);

      var text = document.createElement('p');
      text.className = 'topshelf-ext-reviews__text';
      text.textContent = (review.text && review.text.text) || (review.originalText && review.originalText.text) || '';
      card.appendChild(text);

      var footer = document.createElement('div');
      footer.className = 'topshelf-ext-reviews__card-footer';
      var sourceGroup = document.createElement('span');
      sourceGroup.className = 'topshelf-ext-reviews__card-source';
      var logo = document.createElement('span');
      logo.className = 'topshelf-ext-reviews__card-logo';
      logo.setAttribute('aria-hidden', 'true');
      logo.innerHTML = GOOGLE_LOGO_SVG;
      sourceGroup.appendChild(logo);
      if (review.googleMapsUri) {
        var source = document.createElement('a');
        source.className = 'topshelf-ext-reviews__source';
        source.href = review.googleMapsUri;
        source.target = '_blank';
        source.rel = 'noopener';
        source.textContent = 'Posted on Google';
        sourceGroup.appendChild(source);
      }
      footer.appendChild(sourceGroup);
      if (review.flagContentUri) {
        var report = document.createElement('a');
        report.className = 'topshelf-ext-reviews__report';
        report.href = review.flagContentUri;
        report.target = '_blank';
        report.rel = 'noopener nofollow';
        report.textContent = 'Report';
        footer.appendChild(report);
      }
      card.appendChild(footer);
      return card;
    }

    function loadGoogleLive() {
      if (!googleLivePanel || googleLiveRequested) return;
      googleLiveRequested = true;

      fetch('https://places.googleapis.com/v1/places/' + encodeURIComponent(config.googlePlaceId) + '?languageCode=en', {
        headers: {
          'X-Goog-Api-Key': config.googleApiKey,
          'X-Goog-FieldMask': 'rating,userRatingCount,reviews,googleMapsUri,googleMapsLinks'
        }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Places ' + response.status);
          return response.json();
        })
        .then(function (place) {
          var reviews = (place.reviews || []).filter(function (review) {
            var body = (review.text && review.text.text) || (review.originalText && review.originalText.text);
            return review.rating >= minRating && body;
          });
          if (!place.rating && !reviews.length) throw new Error('No Google reviews');

          var slot = function (name) { return googleLivePanel.querySelector('[data-topshelf-live="' + name + '"]'); };
          var links = place.googleMapsLinks || {};
          var seeAll = links.reviewsUri || place.googleMapsUri;
          if (seeAll) {
            slot('see-all').href = seeAll;
            slot('see-all').hidden = false;
          }
          if (links.writeAReviewUri) {
            slot('write').href = links.writeAReviewUri;
            slot('write').hidden = false;
          }

          var track = slot('track');
          track.textContent = '';
          reviews.forEach(function (review) { track.appendChild(buildGoogleCard(review)); });
          track.removeAttribute('aria-busy');
          updateArrows();
        })
        .catch(function () {
          if (designMode) {
            var track = googleLivePanel.querySelector('[data-topshelf-live="track"]');
            track.innerHTML = '<li class="topshelf-reviews__notice">Google reviews did not load. Check the Places API key, its website restriction and the Place ID. (Only shown in the theme editor.)</li>';
          } else {
            removeGoogleTab();
          }
        });
    }

    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () { selectTab(tab, false); });
      tab.addEventListener('keydown', function (event) {
        var index = tabs.indexOf(tab);
        var next = null;
        if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
        else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
        else if (event.key === 'Home') next = tabs[0];
        else if (event.key === 'End') next = tabs[tabs.length - 1];
        if (!next) return;
        event.preventDefault();
        selectTab(next, true);
      });
    });

    function updateArrows() {
      root.querySelectorAll('.topshelf-ext-reviews__viewport').forEach(function (viewport) {
        var track = viewport.querySelector('.topshelf-ext-reviews__track');
        var prev = viewport.querySelector('[data-topshelf-ext-scroll="-1"]');
        var next = viewport.querySelector('[data-topshelf-ext-scroll="1"]');
        if (!track || !prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      });
    }

    function bindExternalReviewViewports(scope) {
      scope.querySelectorAll('.topshelf-ext-reviews__viewport').forEach(function (viewport) {
        if (viewport.getAttribute('data-topshelf-ext-bound') === 'true') return;
        viewport.setAttribute('data-topshelf-ext-bound', 'true');
        var track = viewport.querySelector('.topshelf-ext-reviews__track');
        if (!track) return;
        viewport.querySelectorAll('[data-topshelf-ext-scroll]').forEach(function (button) {
          button.addEventListener('click', function () {
            var direction = Number(button.getAttribute('data-topshelf-ext-scroll'));
            track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: 'smooth' });
          });
        });
        track.addEventListener('scroll', updateArrows, { passive: true });
      });
    }
    bindExternalReviewViewports(root);
    window.addEventListener('resize', updateArrows, { passive: true });

    function autoScrollTick() {
      if (!autoScroll || document.hidden) return;

      var activePanel = root.querySelector('[data-topshelf-panel]:not([hidden])');
      if (!activePanel) return;

      var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
      if (canHover && root.matches(':hover')) return;
      if (activePanel.contains(document.activeElement)) return;

      if (activePanel === reviewsioPanel) {
        var nativeTrack = mount.querySelector('.R-ReviewsList');
        var pauseButton = mount.querySelector('.js-pause-btn');
        var pauseLabel = pauseButton
          ? (pauseButton.getAttribute('aria-label') || '') + ' ' + (pauseButton.textContent || '')
          : '';
        var isPaused = pauseButton && (
          pauseButton.getAttribute('aria-pressed') === 'true' ||
          /\b(play|resume)\b/i.test(pauseLabel)
        );
        if (!nativeTrack || isPaused) return;

        var nativeMax = nativeTrack.scrollWidth - nativeTrack.clientWidth;
        if (nativeMax > 2 && nativeTrack.scrollLeft >= nativeMax - 2) {
          nativeTrack.scrollTo({ left: 0, behavior: 'smooth' });
        }
        return;
      }

      var track = activePanel.querySelector('.topshelf-ext-reviews__track');
      if (!track) return;
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 2) return;

      if (track.scrollLeft >= max - 2) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        var card = track.querySelector('.topshelf-ext-reviews__card');
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        var distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.9;
        track.scrollBy({ left: distance, behavior: 'smooth' });
      }
    }

    if (autoScroll && !root.__topshelfReviewsAutoScrollTimer) {
      root.__topshelfReviewsAutoScrollTimer = window.setInterval(autoScrollTick, autoScroll);
    }

    function maybeStart() {
      if (started || !nearViewport || !reviewsioPanel || reviewsioPanel.hidden) return;
      started = true;
      start();
    }

    function loadWidget() {
      if (window.topshelfReviewsWidgetLoader) return window.topshelfReviewsWidgetLoader;
      window.topshelfReviewsWidgetLoader = new Promise(function (resolve, reject) {
        var nativeOpen = XMLHttpRequest.prototype.open;
        XMLHttpRequest.prototype.open = function (method, url) {
          var rating = window.topshelfReviewsMinRating;
          if (rating && typeof url === 'string' && url.indexOf('https://api.reviews.co.uk/timeline/data?') === 0) {
            var args = Array.prototype.slice.call(arguments);
            args[1] = url.replace(/&minRating=[^&]*/g, '') + '&minRating=' + rating;
            return nativeOpen.apply(this, args);
          }
          return nativeOpen.apply(this, arguments);
        };

        [
          'https://assets.reviews.io/css/widgets/carousel-widget.css?_t=2025010914',
          'https://assets.reviews.io/iconfont/reviewsio-icons/style.css?_t=2025010914'
        ].forEach(function (href) {
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          document.head.appendChild(link);
        });

        var script = document.createElement('script');
        script.src = 'https://widget.reviews.co.uk/carousel-inline-iframeless/dist.js?_t=2025010914';
        script.async = true;
        script.onload = function () {
          typeof window.carouselInlineWidget === 'function' ? resolve() : reject();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
      return window.topshelfReviewsWidgetLoader;
    }

    function init() {
      window.topshelfReviewsMinRating = Math.max(window.topshelfReviewsMinRating || 0, minRating);

      if (config.photoReviewsOnly) {
        window.reviewsioWidgetState = window.reviewsioWidgetState || {};
        window.reviewsioWidgetState.widgets = window.reviewsioWidgetState.widgets || {};
        window.reviewsioWidgetState.widgets[mount.id] = { sort: 'with_images' };
      }

      new window.carouselInlineWidget(mount.id, {
        store: config.storeKey,
        sku: '',
        lang: 'en',
        carousel_type: 'default',
        styles_carousel: 'CarouselWidget--sideHeader--withcards',
        options: {
          general: {
            review_type: config.reviewType,
            min_reviews: '1',
            max_reviews: config.maxReviews,
            address_format: 'CITY, COUNTRY',
            enable_auto_scroll: autoScroll,
            enable_pause_button: autoScroll !== false,
            enable_richsnippets_company: false,
            enable_richsnippets_product: false
          },
          header: {
            enable_overall_stars: true,
            rating_decimal_places: 2
          },
          reviews: {
            enable_customer_name: true,
            enable_customer_location: true,
            enable_verified_badge: true,
            enable_subscriber_badge: true,
            enable_recommends_badge: true,
            enable_photos: true,
            enable_videos: true,
            enable_review_date: true,
            disable_same_customer: true,
            disable_duplicate_reviews: true,
            min_review_percent: minRating,
            third_party_source: true,
            hide_empty_reviews: true,
            enable_product_name: true,
            tags: config.tags,
            branch: '',
            enable_branch_name: false,
            enable_avatars: false
          },
          popups: {
            enable_review_popups: true,
            enable_helpful_buttons: true,
            enable_helpful_count: true,
            enable_share_buttons: true
          }
        },
        translations: {
          verified_customer: 'Verified Customer'
        },
        styles: {
          '--base-font-size': '15',
          '--base-maxwidth': '100%',

          '--reviewsio-logo-style': 'var(--logo-normal)',

          '--common-star-color': '#ffc63b',
          '--common-star-disabled-color': 'rgba(255,255,255,0.22)',
          '--medium-star-size': '20px',
          '--small-star-size': '18px',
          '--x-small-star-size': '16px',
          '--x-small-star-display': 'inline-flex',

          '--header-width': '0px',

          '--item-maximum-columns': config.maxColumns,
          '--item-background-start-color': config.cardStartColor,
          '--item-background-end-color': config.cardEndColor,
          '--item-gradient-direction': '150deg',
          '--item-padding': '22px',
          '--item-border-width': '1px',
          '--item-border-color': 'rgba(255,255,255,0.08)',
          '--item-border-radius': '16px',
          '--item-shadow-size': '18px',
          '--item-shadow-color': 'rgba(0,25,53,0.16)',

          '--heading-text-color': '#ffffff',
          '--heading-text-font-weight': '700',
          '--heading-text-font-family': 'inherit',
          '--heading-text-line-height': '1.35',
          '--heading-text-letter-spacing': '0',
          '--heading-text-transform': 'none',

          '--body-text-color': 'rgba(255,255,255,0.86)',
          '--body-text-font-weight': '400',
          '--body-text-font-family': 'inherit',
          '--body-text-line-height': '1.5',
          '--body-text-letter-spacing': '0',
          '--body-text-transform': 'none',
          '--body-max-lines': '7',

          '--scroll-button-icon-color': '#001935',
          '--scroll-button-icon-size': '20px',
          '--scroll-button-bg-color': '#ffffff',
          '--scroll-button-border-width': '1px',
          '--scroll-button-border-color': '#e4e7ed',
          '--scroll-button-border-radius': '999px',
          '--scroll-button-shadow-size': '14px',
          '--scroll-button-shadow-color': 'rgba(0,25,53,0.18)',
          '--scroll-button-horizontal-position': '12px',
          '--scroll-button-vertical-position': '0px',

          '--badge-icon-color': '#ffc63b',
          '--badge-icon-font-size': '15px',
          '--badge-text-color': 'rgba(255,255,255,0.86)',
          '--badge-text-font-size': 'inherit',
          '--badge-text-letter-spacing': 'inherit',
          '--badge-text-transform': 'inherit',

          '--author-font-size': 'inherit',
          '--author-font-weight': '700',
          '--author-text-transform': 'inherit',

          '--photo-video-thumbnail-size': '80px',
          '--photo-video-thumbnail-border-radius': '10px',

          '--popup-backdrop-color': 'rgba(0,25,53,0.72)',
          '--popup-color': '#ffffff',
          '--popup-star-color': '#ffc63b',
          '--popup-disabled-star-color': 'rgba(0,25,53,0.16)',
          '--popup-heading-text-color': '#001935',
          '--popup-body-text-color': '#2b3a4d',
          '--popup-badge-icon-color': '#d99400',
          '--popup-badge-icon-font-size': '19px',
          '--popup-badge-text-color': '#3b4b5f',
          '--popup-badge-text-font-size': '14px',
          '--popup-border-width': '0px',
          '--popup-border-color': 'rgba(0,0,0,0.1)',
          '--popup-border-radius': '16px',
          '--popup-shadow-size': '40px',
          '--popup-shadow-color': 'rgba(0,25,53,0.28)',
          '--popup-icon-color': '#001935',

          '--tooltip-bg-color': '#001935',
          '--tooltip-text-color': '#ffffff'
        }
      });

      window.setTimeout(function () {
        if (mount.querySelector('.CarouselWidget-prefix')) {
          mount.removeAttribute('aria-busy');
        } else {
          fail();
        }
      }, 12000);
    }

    function start() {
      loadWidget().then(function () {
        try {
          init();
        } catch (error) {
          fail();
        }
      }, fail);
    }

    updateArrows();

    if (!('IntersectionObserver' in window)) {
      nearViewport = true;
      maybeStart();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
        observer.disconnect();
        nearViewport = true;
        maybeStart();
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(root);
  
  }

  function schedule(root) {
    if (!(root instanceof HTMLElement) || root.dataset.topshelfReviewsScheduled === 'true') return;
    root.dataset.topshelfReviewsScheduled = 'true';
    var configNode = root.querySelector('[data-topshelf-reviews-config]');
    var designMode = false;
    try { designMode = JSON.parse(configNode?.textContent || '{}').designMode === true; } catch (error) {}
    if (designMode || !('IntersectionObserver' in window)) return initReviews(root);
    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      initReviews(root);
    }, { rootMargin: '1000px 0px' });
    observer.observe(root);
  }

  function bindAll(scope) {
    if (scope instanceof HTMLElement && scope.matches('[id^="topshelf-reviews-"]')) schedule(scope);
    (scope || document).querySelectorAll?.('[id^="topshelf-reviews-"]').forEach(schedule);
  }
  var boot = function () { bindAll(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', function (event) { bindAll(event.target); });
})();

(function initTopshelfProductAccessibility() {
  if (window.__topshelfProductAccessibilityBound) return;

  var productMain = document.querySelector('main[data-template^="product"]');
  if (!productMain) return;
  window.__topshelfProductAccessibilityBound = true;

  var gallerySequence = 0;
  var enhancementScheduled = false;

  function syncThumbnailTab(button) {
    var selected = button.getAttribute('aria-selected') === 'true';
    button.tabIndex = selected ? 0 : -1;

    var panelId = button.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;
    if (panel) panel.tabIndex = selected ? 0 : -1;
  }

  function handleThumbnailKeydown(event) {
    var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (keys.indexOf(event.key) === -1) return;

    var tabs = Array.prototype.slice.call(
      event.currentTarget.parentElement.querySelectorAll('[role="tab"], [role="option"]')
    );
    var current = tabs.indexOf(event.currentTarget);
    if (current === -1 || tabs.length < 2) return;

    var next = current;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;

    event.preventDefault();
    tabs[next].focus();
    tabs[next].click();
  }

  function enhanceMediaGallery(gallery) {
    if (!(gallery instanceof HTMLElement)) return;

    if (!gallery.dataset.topshelfA11yId) {
      gallerySequence += 1;
      gallery.dataset.topshelfA11yId = 'topshelf-product-media-' + gallerySequence;
    }

    var galleryId = gallery.dataset.topshelfA11yId;
    var groups = Array.prototype.slice.call(
      gallery.querySelectorAll('.slideshow-controls__thumbnails')
    );

    groups.forEach(function (group, groupIndex) {
      var slideshow = group.closest('slideshow-component');
      if (!slideshow) return;

      var slides = Array.prototype.slice.call(
        slideshow.querySelectorAll(
          ':scope > slideshow-container > slideshow-slides > slideshow-slide'
        )
      );
      var tabs = Array.prototype.slice.call(
        group.querySelectorAll('.slideshow-controls__thumbnail[aria-selected]')
      );

      group.setAttribute('role', 'tablist');
      group.setAttribute('aria-label', 'Product images');
      group.setAttribute('aria-orientation', 'horizontal');

      tabs.forEach(function (button, index) {
        var panel = slides[index];
        var tabId = galleryId + '-tab-' + groupIndex + '-' + index;
        var panelId = galleryId + '-panel-' + index;

        button.setAttribute('role', 'tab');
        button.id = tabId;
        if (panel) {
          panel.id = panelId;
          panel.setAttribute('role', 'tabpanel');
          if (groupIndex === 0) panel.setAttribute('aria-labelledby', tabId);
          button.setAttribute('aria-controls', panelId);
        }

        syncThumbnailTab(button);
        if (button.dataset.topshelfA11yBound !== 'true') {
          button.dataset.topshelfA11yBound = 'true';
          button.addEventListener('keydown', handleThumbnailKeydown);
        }
      });
    });
  }

  function enhanceZoomThumbnails(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var groups = [];
    if (root instanceof HTMLElement && root.matches('.dialog-thumbnails-list')) {
      groups.push(root);
    }
    root.querySelectorAll('.dialog-thumbnails-list').forEach(function (group) {
      groups.push(group);
    });

    groups.forEach(function (group) {
      group.setAttribute('role', 'listbox');
      group.setAttribute('aria-label', 'Zoomed product images');
      group.setAttribute('aria-orientation', 'horizontal');

      group
        .querySelectorAll('.dialog-thumbnails-list__thumbnail[aria-selected]')
        .forEach(function (button) {
          button.setAttribute('role', 'option');
          syncThumbnailTab(button);
          if (button.dataset.topshelfA11yBound !== 'true') {
            button.dataset.topshelfA11yBound = 'true';
            button.addEventListener('keydown', handleThumbnailKeydown);
          }
        });
    });
  }

  function fixReviewVerificationBadges(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    if (
      root instanceof HTMLElement &&
      root.matches('.R-LabelTag--shopVerified[aria-label]')
    ) {
      root.setAttribute('role', 'img');
    }
    root
      .querySelectorAll('.R-LabelTag--shopVerified[aria-label]')
      .forEach(function (badge) {
        badge.setAttribute('role', 'img');
      });
  }

  function fixCartBubbleIds() {
    var buttons = Array.prototype.slice.call(
      document.querySelectorAll(
        'button[data-kith-cart-open], .header-actions__action.action__cart'
      )
    );

    buttons.forEach(function (button, index) {
      var text = button.querySelector('.cart-bubble__text');
      if (!text) return;

      var id = 'topshelf-cart-bubble-text-' + (index + 1);
      text.id = id;

      var describedBy = (button.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(function (token) {
          return token && token !== 'cart-bubble-text' && token.indexOf('topshelf-cart-bubble-text-') !== 0;
        });
      describedBy.push(id);
      button.setAttribute('aria-describedby', describedBy.join(' '));
    });
  }

  function enhance(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    if (root instanceof HTMLElement && root.matches('media-gallery')) {
      enhanceMediaGallery(root);
    }
    root.querySelectorAll('media-gallery').forEach(enhanceMediaGallery);
    enhanceZoomThumbnails(root);
    fixReviewVerificationBadges(root);
    fixCartBubbleIds();
  }

  function scheduleEnhancement() {
    if (enhancementScheduled) return;
    enhancementScheduled = true;
    requestAnimationFrame(function () {
      enhancementScheduled = false;
      enhance(productMain);
    });
  }

  var boot = function () {
    enhance(document);

    var observer = new MutationObserver(function (mutations) {
      var hasAddedNodes = false;
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.target.matches?.('[role="tab"], [role="option"]')
        ) {
          syncThumbnailTab(mutation.target);
        }
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-label' &&
          mutation.target.matches?.('.R-LabelTag--shopVerified')
        ) {
          mutation.target.setAttribute('role', 'img');
        }
        if (mutation.addedNodes.length) hasAddedNodes = true;
      });
      if (hasAddedNodes) scheduleEnhancement();
    });

    observer.observe(productMain, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-selected', 'aria-label'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    enhance(event.target);
  });
})();
